// Anita Week 8 / Day 1 — Living and Nonliving.
// Content is supplied verbatim by the FINAL Week 8 Part A source-of-truth
// package; this file only places that content into the Nimny Lab schema.
// No pedagogy is invented or simplified.
// SAFETY: notebook/window observation only; the parentGuide explicitly tells
// the parent not to let the child touch / eat anything found outside.

const day1 = {
  id: 'day-1',
  studentId: 'anita',
  weekId: 'week-8',
  dayId: 'day-1',
  status: 'active',
  title: 'Живое и неживое / Living and Nonliving',
  shortTitle: 'Живое и неживое / Living and Nonliving',
  theme:
    'Анита учится отличать живую и неживую природу, искать признаки живого и считать данные наблюдения. / Anita learns to tell living and nonliving nature apart, look for signs of life, and count observation data.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'nature', 'classification', 'data', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни вышла к окну лаборатории и увидела дерево, камень, птицу, облако и цветок. Сегодня Анита узнает, что живое растёт, дышит, питается, изменяется и может иметь потомство, а неживое не растёт как организм. / Nimny looks out of the lab window and sees a tree, a rock, a bird, a cloud and a flower. Today Anita learns that living things grow, breathe, need food or energy, change, and can have offspring; nonliving things do not grow like organisms.',
  goals: [
    'Понять признаки живого. / Understand signs of living things.',
    'Отличать живую и неживую природу. / Tell living and nonliving nature apart.',
    'Сортировать объекты по признакам. / Sort objects by traits.',
    'Читать таблицу наблюдений. / Read an observation table.',
    'Решать задачи с группами, умножением и делением. / Solve grouping, multiplication and division tasks.',
    'Сделать карточку наблюдателя природы. / Make a nature observer card.'
  ],
  vocabulary: [
    { term: 'живое / living', definition: 'то, что растёт, дышит, питается и изменяется. / what grows, breathes, needs food and changes.' },
    { term: 'неживое / nonliving', definition: 'то, что не живёт как организм. / what does not live like an organism.' },
    { term: 'природа / nature', definition: 'мир вокруг нас: растения, животные, вода, воздух, камни. / the world around us.' },
    { term: 'растёт / grows', definition: 'становится больше или развивается. / becomes bigger or develops.' },
    { term: 'дышит / breathes', definition: 'получает воздух или газ для жизни. / takes air for life.' },
    { term: 'питается / needs food or energy', definition: 'получает питание или энергию. / takes food or energy.' },
    { term: 'организм / organism', definition: 'живое тело: растение, животное, гриб или бактерия. / a living body.' },
    { term: 'признак / trait or sign', definition: 'то, по чему мы узнаём объект. / what we identify an object by.' },
    { term: 'наблюдение / observation', definition: 'когда мы смотрим внимательно и записываем. / careful watching and recording.' },
    { term: 'вывод / conclusion', definition: 'что я понял после наблюдения. / what I understood after observing.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова природы / Nature Words',
      prompt: 'Прочитай по-русски:\nживое\nнеживое\nприрода\nрастёт\nдышит\nпитается\nорганизм\nпризнак\nнаблюдение\nвывод',
      ruPrimaryText: 'Прочитай 10 русских слов природы.',
      enHelpText: 'Read the Russian nature words first. English is only for help.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nЖивое растёт и изменяется.\nЖивому нужна вода, воздух или питание.\nКамень не живёт как организм.\nНаблюдение помогает сделать вывод.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про живое и неживое.',
      enHelpText: 'Living things grow and change. Living things need water, air, or food/energy. A rock does not live like an organism. Observation helps make a conclusion.',
      answer: 'Анита пробует читать 4 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'living-traits-check',
      type: 'text',
      title: 'Задание 3 — Признаки живого / Signs of Living Things',
      prompt:
        'Прочитай признаки.\n\nЖивое обычно:\n1. растёт;\n2. нуждается в воде, воздухе или питании;\n3. изменяется;\n4. может иметь потомство;\n5. реагирует на мир вокруг.\n\nОтветь:\nКакие 3 признака живого ты запомнила?',
      ruPrimaryText: 'Назови 3 любых признака живого.',
      enHelpText: 'Name any 3 living-thing signs from the list (grows, needs water/air/food, changes, has offspring, reacts).',
      answer: 'Любые 3 правильных признака: растёт, питается, изменяется, дышит, может иметь потомство.'
    },
    {
      id: 'nature-sort',
      type: 'classify',
      title: 'Задание 4 — Живое или неживое? / Living or Nonliving?',
      prompt:
        'Разложи карточки по категориям.\n\nКатегории:\nЖивое\nНеживое\nБыло живым или часть живого\n\nКарточки:\n1. дерево\n2. камень\n3. птица\n4. облако\n5. цветок\n6. вода\n7. кошка\n8. деревянная палочка\n9. лист, который упал с дерева\n10. гриб\n11. песок\n12. семя',
      ruPrimaryText: 'Разложи 12 карточек: живое / неживое / было живым.',
      enHelpText: 'Sort 12 cards into Living / Nonliving / Was-living-or-part-of-living.',
      categories: [
        { id: 'living', label: 'Живое / Living' },
        { id: 'nonliving', label: 'Неживое / Nonliving' },
        { id: 'was-living', label: 'Было живым или часть живого / Was living or part of living' }
      ],
      items: [
        { id: 'i1', label: 'дерево', category: 'living' },
        { id: 'i2', label: 'камень', category: 'nonliving' },
        { id: 'i3', label: 'птица', category: 'living' },
        { id: 'i4', label: 'облако', category: 'nonliving' },
        { id: 'i5', label: 'цветок', category: 'living' },
        { id: 'i6', label: 'вода', category: 'nonliving' },
        { id: 'i7', label: 'кошка', category: 'living' },
        { id: 'i8', label: 'деревянная палочка', category: 'was-living' },
        { id: 'i9', label: 'лист, который упал с дерева', category: 'was-living' },
        { id: 'i10', label: 'гриб', category: 'living' },
        { id: 'i11', label: 'песок', category: 'nonliving' },
        { id: 'i12', label: 'семя', category: 'living' }
      ],
      answer: 'Живое: 1, 3, 5, 7, 10, 12. Неживое: 2, 4, 6, 11. Было живым: 8, 9. Parent note: семя может быть живым, если может прорасти; упавший лист уже не живёт как часть растения, но был частью живого.'
    },
    {
      id: 'math-nature-table',
      type: 'text',
      title: 'Задание 5 — Таблица наблюдений / Observation Table',
      prompt:
        'Нимни записала наблюдения:\n\nрастения — 8\nживотные — 6\nгрибы — 3\nнеживые объекты — 9\n\nОтветь:\n1. Сколько всего живых организмов? Растения + животные + грибы.\n2. Сколько всего объектов в таблице?\n3. На сколько неживых объектов больше, чем грибов?\n4. Если добавить ещё 4 растения, сколько растений станет?',
      ruPrimaryText: 'Реши 4 задачи по таблице наблюдений.',
      enHelpText: 'Table: 8 plants, 6 animals, 3 mushrooms, 9 nonliving. Answer 4 questions.',
      answer: '1) 8 + 6 + 3 = 17. 2) 17 + 9 = 26. 3) 9 − 3 = 6. 4) 8 + 4 = 12.'
    },
    {
      id: 'math-equal-groups-nature',
      type: 'text',
      title: 'Задание 6 — Равные группы природы / Equal Nature Groups',
      prompt:
        'Анита рисует карточки природы.\n\nУ неё 4 ряда растений.\nВ каждом ряду по 5 растений.\nЕщё она рисует 3 ряда животных.\nВ каждом ряду по 4 животных.\n\nОтветь:\n1. Сколько растений?\n2. Сколько животных?\n3. Сколько всего растений и животных?\n4. Если все 32 карточки разложить поровну в 4 коробки, сколько карточек в каждой коробке?',
      ruPrimaryText: 'Реши 4 задачи на умножение и деление в равные группы.',
      enHelpText: '4 rows × 5 plants and 3 rows × 4 animals. 32 cards ÷ 4 boxes. Answer 4 questions.',
      answer: '1) 4 × 5 = 20. 2) 3 × 4 = 12. 3) 20 + 12 = 32. 4) 32 ÷ 4 = 8.'
    },
    {
      id: 'data-bar-graph-living',
      type: 'text',
      title: 'Задание 7 — Мини-график живого и неживого / Mini Graph',
      prompt:
        'Сделай мини-график словами или в тетради.\n\nДанные:\nживое — 6\nнеживое — 4\nбыло живым — 2\n\nНарисуй квадратики:\nживое: □ □ □ □ □ □\nнеживое: □ □ □ □\nбыло живым: □ □\n\nОтветь:\n1. Какая группа самая большая?\n2. На сколько «живое» больше, чем «было живым»?',
      ruPrimaryText: 'Прочитай мини-график и ответь на 2 вопроса.',
      enHelpText: 'Mini bar graph: living=6, nonliving=4, was-living=2. Answer 2 questions.',
      answer: '1) Живое. 2) 6 − 2 = 4.'
    },
    {
      id: 'spelling-living',
      type: 'text',
      title: 'Задание 8 — Правописание / Spelling',
      prompt: 'Напиши:\nживое\nприрода\nвывод\nлист\nкамень\n\nВставь буквы:\nж_вое\nпр_рода\nвыв_д\nл_ст\nк_мень',
      ruPrimaryText: 'Напиши 5 слов природы и вставь пропущенные буквы.',
      enHelpText: 'Write 5 words and fill in the missing letters.',
      answer: 'живое, природа, вывод, лист, камень.'
    },
    {
      id: 'creative-nature-observer-card',
      type: 'text',
      title: 'Задание 9 — Карточка наблюдателя природы / Nature Observer Card',
      prompt:
        'Сделай карточку наблюдателя.\n\nНарисуй 3 объекта:\n1. один живой объект;\n2. один неживой объект;\n3. один объект, который был частью живого.\n\nНапиши:\nЖивое: ______.\nНеживое: ______.\nБыло частью живого: ______.\nМой вывод: ______.',
      ruPrimaryText: 'Нарисуй 3 объекта, подпиши и напиши один вывод.',
      enHelpText: 'Draw 3 objects (living, nonliving, was-part-of-living), label them and write one conclusion.',
      hint: 'Наблюдай из окна или используй рисунки — не трогай неизвестные растения или животных. / Observe from window or use drawings; do not touch unknown plants or animals.',
      answer: 'Open-ended. Accept if Anita draws three objects, labels them, and gives a simple conclusion.'
    },
    {
      id: 'speech-living-explain',
      type: 'text',
      title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nЖивое растёт и изменяется.\nНеживое не живёт как организм.\nМой пример живого: ______.\nМой пример неживого: ______.',
      ruPrimaryText: 'Объясни родителю один пример живого и один пример неживого по-русски.',
      enHelpText: 'Tell a parent one living example and one nonliving example in Russian.',
      answer: 'Accept if Anita explains at least one living and one nonliving example in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 4 фразы.',
    '3. Запиши 3 признака живого.',
    '4. Разложи 12 карточек устно или в тетради.',
    '5. Реши задачи по таблице.',
    '6. Нарисуй мини-график.',
    '7. Сделай карточку наблюдателя.',
    '8. Объясни родителю один пример живого и неживого.'
  ],
  printWorksheet: {
    title: 'Живое и неживое / Living and Nonliving',
    subtitle: 'Неделя 8 · День 1 / Week 8 · Day 1',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни смотрит на мир природы и учится отличать живое от неживого. / Nimny looks at the world of nature and learns to tell living from nonliving.' },
          { type: 'text', text: 'Задание 1 — Слова природы. Прочитай и обведи:' },
          { type: 'list', items: [
            'живое / living',
            'неживое / nonliving',
            'природа / nature',
            'растёт / grows',
            'дышит / breathes',
            'питается / needs food or energy',
            'организм / organism',
            'признак / sign',
            'наблюдение / observation',
            'вывод / conclusion'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку:' },
          { type: 'list', items: [
            '□ Живое растёт и изменяется.',
            '□ Живому нужна вода, воздух или питание.',
            '□ Камень не живёт как организм.',
            '□ Наблюдение помогает сделать вывод.'
          ] },
          { type: 'text', text: 'Задание 3 — Признаки живого. Напиши 3 признака живого:' },
          { type: 'list', items: [
            '1. __________________________',
            '2. __________________________',
            '3. __________________________'
          ] }
        ]
      },
      {
        title: 'Сортировка, таблица и график (математика природы) / Sort, Table and Graph',
        blocks: [
          { type: 'text', text: 'Задание 4 — Живое или неживое? Напиши Ж = живое, Н = неживое, Б = было живым.' },
          { type: 'list', items: [
            '1. дерево ____',
            '2. камень ____',
            '3. птица ____',
            '4. облако ____',
            '5. цветок ____',
            '6. вода ____',
            '7. кошка ____',
            '8. деревянная палочка ____',
            '9. лист, который упал с дерева ____',
            '10. гриб ____',
            '11. песок ____',
            '12. семя ____'
          ] },
          { type: 'text', text: 'Задание 5 — Таблица наблюдений. Растения — 8, животные — 6, грибы — 3, неживые объекты — 9.' },
          { type: 'list', items: [
            '1. Живые организмы: 8 + 6 + 3 = ______',
            '2. Всего объектов: ______ + 9 = ______',
            '3. Неживых больше, чем грибов: 9 − 3 = ______',
            '4. Если добавить 4 растения: 8 + 4 = ______'
          ] },
          { type: 'text', text: 'Задание 6 — Равные группы природы (умножение, деление):' },
          { type: 'list', items: [
            '4 ряда растений × 5 растений = ______',
            '3 ряда животных × 4 животных = ______',
            'Всего карточек: ______ + ______ = ______',
            '32 карточки ÷ 4 коробки = ______'
          ] },
          { type: 'text', text: 'Задание 7 — Мини-график. живое: □ □ □ □ □ □ ; неживое: □ □ □ □ ; было живым: □ □.' },
          { type: 'list', items: [
            'Самая большая группа: __________________________',
            'Живое больше, чем было живым, на: ______'
          ] }
        ]
      },
      {
        title: 'Карточка наблюдателя / Observer Card',
        blocks: [
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'живое   природа   вывод   лист   камень',
            'ж_вое     пр_рода     выв_д     л_ст     к_мень'
          ] },
          { type: 'text', text: 'Задание 9 — Карточка наблюдателя природы. Нарисуй 3 объекта: живой, неживой, был частью живого.' },
          { type: 'list', items: [
            'Живое: __________________________',
            'Неживое: __________________________',
            'Было частью живого: __________________________'
          ] },
          { type: 'text', text: 'Мой вывод: __________________________________________________________' },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Скажи родителю:' },
          { type: 'list', items: [
            '□ Живое растёт и изменяется.',
            '□ Неживое не живёт как организм.',
            '□ Мой пример живого: __________________________',
            '□ Мой пример неживого: __________________________'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 4 фразы  □ назвала 3 признака живого  □ разложила карточки  □ решила math tasks  □ сделала карточку наблюдателя  □ объяснила родителю.' },
          { type: 'text', text: 'Notebook Mode fallback — если нет принтера, сделай все 10 заданий в тетради по тем же номерам.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает признаки живого и умеет отличать живую и неживую природу.',
    mathGoal: 'Сложение, вычитание, умножение равных групп (4 × 5, 3 × 4), деление 32 ÷ 4, таблица данных и мини-график.',
    russianGoal: 'Прочитать слова живое, неживое, природа, растёт, дышит, питается, организм, признак, наблюдение, вывод.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 5 слов, подписать карточку наблюдателя.',
    practicalGoal: 'Отсортировать объекты природы и сделать вывод.',
    parentTheoryCard: {
      title: 'Как объяснить живое и неживое',
      purpose: 'Помочь родителю объяснить признаки живого без перегруза и без спора о сложных биологических исключениях.',
      keyMessage: 'Живое обычно растёт, изменяется, нуждается в воде, воздухе или питании и является организмом. Неживое не живёт как организм.',
      parentScript: [
        '«Живое — это организм: растение, животное, гриб или бактерия.»',
        '«Живое растёт и изменяется.»',
        '«Живому нужны условия для жизни.»',
        '«Неживое может быть частью природы, но оно не живёт как организм.»',
        '«Некоторые вещи были частью живого: палочка, сухой лист.»'
      ],
      practiceSteps: [
        'Разберите 3 признака живого.',
        'Отсортируйте несколько карточек.',
        'Обсудите один tricky example: семя или сухой лист.',
        'Попросите Аниту сказать вывод по-русски.'
      ],
      doNotSay: [
        'Не спорить долго о сложных исключениях.',
        'Не требовать взрослую биологическую точность.',
        'Не разрешать трогать неизвестные растения или животных.'
      ],
      paperOnlyNotes: [
        'Можно выполнять в тетради.',
        'Можно наблюдать из окна.',
        'Не собирать растения и не трогать животных без родителя.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится классифицировать объекты природы по признакам.',
    keyMessage: 'Признаки помогают думать: я смотрю, сравниваю и делаю вывод.',
    howToExplain: 'Скажите: «Учёный не просто говорит „это дерево“. Он спрашивает: какие признаки показывают, что это живое?»',
    exampleDialogue:
      'Parent: Дерево живое?\nChild: Да.\nParent: Почему?\nChild: Оно растёт.\nParent: Камень живой?\nChild: Нет.\nParent: Почему?\nChild: Он не растёт как организм.',
    readingSupport: 'Слова «организм», «наблюдение», «питается» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Живое растёт и изменяется. Неживое не живёт как организм.»',
    mathAnswerKey:
      'math-nature-table: 1) 17. 2) 26. 3) 6. 4) 12.\nmath-equal-groups-nature: 1) 20. 2) 12. 3) 32. 4) 8.\ndata-bar-graph-living: 1) Живое. 2) 4.',
    spellingAnswerKey: 'живое\nприрода\nвывод\nлист\nкамень',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 10 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 4 фраз.' },
      { taskId: 'living-traits-check', answer: 'Любые 3 признака: растёт, питается, изменяется, дышит, может иметь потомство.' },
      { taskId: 'nature-sort', answer: 'Живое: 1,3,5,7,10,12. Неживое: 2,4,6,11. Было живым: 8,9.' },
      { taskId: 'math-nature-table', answer: '17; 26; 6; 12.' },
      { taskId: 'math-equal-groups-nature', answer: '20; 12; 32; 8.' },
      { taskId: 'data-bar-graph-living', answer: 'Живое; 4.' },
      { taskId: 'spelling-living', answer: 'живое, природа, вывод, лист, камень.' },
      { taskId: 'creative-nature-observer-card', answer: 'Карточка с 3 объектами и выводом.' },
      { taskId: 'speech-living-explain', answer: 'Объяснение живого и неживого по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита назвала признаки живого.',
      'Анита отсортировала объекты.',
      'Анита решила math tasks.',
      'Анита сделала карточку наблюдателя.',
      'Анита объяснила вывод по-русски.'
    ],
    spellingCheck: 'Проверить живое, природа, вывод, лист, камень.',
    readingCheck: 'Главное — попытка русского чтения и понимание.',
    practicalCheck: 'Анита должна понимать, что классификация строится по признакам.',
    drawingCriteria: 'Есть 3 объекта, подписи и вывод.',
    commonMistakes: [
      '«Вода живая, потому что движется.» Правильно: «Вода движется, но не живёт как организм.»',
      '«Палочка живая.» Правильно: «Палочка была частью живого дерева, но сейчас не живёт.»',
      '«Семя неживое.» Правильно: «Семя может быть живым, если может прорасти.»'
    ],
    discussionPrompts: [
      'Почему дерево живое?',
      'Почему камень неживой?',
      'Что значит «было частью живого»?',
      'Какие признаки живого ты запомнила?'
    ],
    ifEasy: 'Попросить Аниту добавить 4 новых объекта и сделать таблицу с 3 категориями.',
    ifDifficult: 'Оставить только 6 карточек: дерево, камень, птица, вода, лист, кошка.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за ясный вывод или аккуратную карточку.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не требовать сложной биологии. Цель — признаки, сортировка и вывод.',
    safetyNotes: 'Не трогать неизвестные растения, животных, грибы, семена или листья без родителя. Не пробовать ничего на вкус.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 },
      { id: 'neat-observer-card', label: 'Бонус за аккуратную карточку наблюдателя', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'living-traits-check',
      'nature-sort',
      'math-nature-table',
      'math-equal-groups-nature',
      'data-bar-graph-living',
      'spelling-living',
      'creative-nature-observer-card',
      'speech-living-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-8-day-2'
};

export default day1;
